import React, { useState, useEffect, useCallback } from "react";
import { getCurrentDate, formatToDate } from "../../Utilitie";
import { FaEraser, FaUpload } from "react-icons/fa";
import Loading from "../../Extra/Loading";
import { MdInsertChart } from "react-icons/md";
import SuccessMsg from "../../Extra/SuccessMsg";
import FailedMsg from "../../Extra/FailedMsg";
import { useGlobalContext } from "../../Context/GlobalContext";

const PDDInsertion = () => {
  const urlPrefix = process.env.REACT_APP_URL_PREFIX;
  const cols = 6;
  const currentDate = getCurrentDate();
  const { userData } = useGlobalContext();
  const [result, setResult] = useState(null);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(7);
  const [globalDate, setGlobalDate] = useState(currentDate);
  const [show, setShow] = useState(false);
  const [messageDisplay, setMessageDisplay] = useState(false);
  const [calledClearAll, setCalledClearAll] = useState(false);
  const [total_time, setTotal_time] = useState(0);
  const [overallAvgTime, setOverallAvgTime] = useState(0);
  const [overallTotalTime, setOverallTotalTime] = useState(0);
  const [sectionData, setSectionData] = useState([]);
  const [error, setError] = useState(false);
  const section = [
    "JABALPUR JN.",
    "NEW KATNI JN.",
    "SATNA JN.",
    "SAUGOR",
    "BEOHARI",
    "SINGRAULI",
    "SECR",
  ];
  const columnHead = [
    "PDD date",
    "Section",
    "Trains count",
    "Avg time",
    "Total time",
  ];

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await fetch(`${urlPrefix}getstationcode`);
      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();
      setSectionData(data.data || []);
    } catch (error) {
      console.error("Error fetching section data:", error);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [urlPrefix]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!sectionData || !sectionData.length) return;

    setGridData((prevGrid) =>
      prevGrid.map((row, rowIndex) => {
        const sectionCode = section[rowIndex];
        const matched = sectionData.find((sec) => sec.station === sectionCode);
        const sectionId = matched ? matched.id : "";
        const updatedRow = [...row];
        updatedRow[2] = sectionId; // Set ID in column index 2
        updatedRow[6] = userData.id;
        return updatedRow;
      })
    );
  }, [sectionData, calledClearAll]);
  const initializeGrid = (rowCount) =>
    section
      .slice(0, rowCount)
      .map((sec) => [globalDate, sec, "", "", "00:00", ""]);

  useEffect(() => {
    const updatedData = gridData.map((row) => [globalDate, ...row.slice(1)]);
    setGridData(updatedData);
  }, [globalDate]);

  useEffect(() => {
    if (result?.status === "ok") {
      setMessageDisplay(true);
      setTimeout(() => setMessageDisplay(false), 4000);
    }
  }, [result]);

  const handleRowChange = (e) => {
    const newRowCount = parseInt(e.target.value, 10);
    if (newRowCount < 1 || newRowCount > section.length) return;
    setRows(newRowCount);
    setGridData(initializeGrid(newRowCount));
  };

  const handleChange = (row, col, value) => {
    const newData = [...gridData];

    newData[row][col] = col === 4 && value === "" ? "00:00" : value;

    // Calculate total time in minutes if train count or avg time changed
    if (col === 3 || col === 4) {
      const trainCount = Number(newData[row][3]);
      const avgTime = newData[row][4];
      if (avgTime && trainCount) {
        const [h, m] = avgTime.split(":").map(Number);
        const totalMinutes = (h * 60 + m) * trainCount;
        newData[row][5] = totalMinutes;
      } else {
        newData[row][5] = 0; // clear if incomplete
      }
    }

    setGridData(newData);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const clipboardData = e.clipboardData.getData("text");
    const rowsData = clipboardData.split("\n").map((row) => row.split("\t"));
    const newGridData = [...gridData];
    rowsData.forEach((row, rIndex) => {
      if (rIndex >= rows) return;
      row.forEach((cell, cIndex) => {
        if (cIndex >= cols - 1) return;
        let value = cell.trim();
        if (cIndex === 0) value = formatToDate(value);
        if (cIndex === 3) value = value === "" ? "00:00" : value; // Default Avg time to "00:00"
        newGridData[rIndex][cIndex + 1] = value;
      });
    });
    setGridData(newGridData);
  };

  const clearAll = () => {
    setGridData(initializeGrid(rows));
    setCalledClearAll(!calledClearAll);
  };
  const [gridData, setGridData] = useState(() => initializeGrid(rows));
  const sendToBackend = async () => {
    const newGrid = gridData.map((rowData) => {
      const transformedRow = [...rowData]; // Clone the row

      // Swap section (index 1) and id (index 2)
      transformedRow[1] = rowData[2]; // id
      transformedRow[2] = rowData[1]; // section

      // Convert trainCount (index 3) to number
      transformedRow[3] = Number(rowData[3]);

      // Handle rowData[5]: convert to number, default to 0 if empty or undefined
      transformedRow[5] = Number(
        rowData[5] === "" || rowData[5] == null ? 0 : rowData[5]
      );

      return transformedRow;
    });

    setLoading(true);

    let response;
    try {
      response = await fetch(`${urlPrefix}insert/pddData`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newGrid),
      });

      const dataResult = await response.json();
      setResult(dataResult);
      setShow(true);
      if (dataResult.status === "ok") {
        clearAll();
      }
    } catch (error) {
      setResult({
        status: "failed",
        message: "Failed to insert data",
        error: error,
      });
      setShow(true);
      console.error("Error sending data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (result?.insertion) clearAll();
  }, [result]);

  useEffect(() => {
    let totalMinutes = 0;
    gridData.forEach((row) => {
      const trainCount = Number(row[3]);
      const avgTime = row[4];
      if (avgTime && trainCount) {
        const [hoursStr, minutesStr] = avgTime.split(":");
        const avgMinutes = Number(hoursStr) * 60 + Number(minutesStr);
        totalMinutes += avgMinutes * trainCount;
      }
    });
    setTotal_time(totalMinutes);
    setOverallTotalTime(totalMinutes);

    const totalTrains = gridData.reduce(
      (acc, row) => acc + (Number(row[3]) || 0),
      0
    );
    setOverallAvgTime(totalTrains > 0 ? totalMinutes / totalTrains : 0);
  }, [gridData]);
  const minutesToHHMM = (minutes) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  };
  return (
    <div className="container mt-12" style={{ minHeight: "90vh" }}>
      <div className="papaDiv text-start ms-3 me-3">
        <h4 className="ms-4">
          <MdInsertChart /> PDD
        </h4>
        <h5 className="ps-2">
          Insert data in table{" "}
          <b style={{ color: "red" }}>"t_predeparture_detention"</b>
        </h5>
      </div>

      <div className="table-responsive">
        {loading && <Loading />}
        <table
          className="table table-striped table-hover"
          border="1"
          cellPadding="1"
          onPaste={handlePaste}
        >
          <thead>
            <tr className="table-dark" style={{ fontSize: "12px" }}>
              {columnHead.map((head, index) => (
                <th key={index} style={{ border: "1px solid gray" }}>
                  {head}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {gridData.map((row, rowIndex) => {
              const trainCount = Number(row[3]);
              const avgTime = row[4];

              let rowTotalMinutes = 0;
              if (avgTime && trainCount) {
                const [hoursStr, minutesStr] = avgTime.split(":");
                const avgMinutes = Number(hoursStr) * 60 + Number(minutesStr);
                rowTotalMinutes = avgMinutes * trainCount;
              }

              const displayTime = `${String(
                Math.floor(rowTotalMinutes / 60)
              ).padStart(2, "0")}:${String(rowTotalMinutes % 60).padStart(
                2,
                "0"
              )}`;

              return (
                <tr key={rowIndex}>
                  {row.map((cell, colIndex) => {
                    if (colIndex === 2 || colIndex === 6) {
                      // Skip rendering Section ID column cells
                      return null;
                    }

                    let matchedSection = sectionData?.find(
                      (sec) => sec.station_code === section[rowIndex]
                    );

                    if (colIndex === 0) {
                      return (
                        <td key={colIndex} style={{ border: "1px solid gray" }}>
                          <input
                            type="date"
                            value={globalDate}
                            style={{ width: "110px" }}
                            max={currentDate}
                            onChange={(e) => setGlobalDate(e.target.value)}
                          />
                        </td>
                      );
                    } else if (colIndex === 1) {
                      return (
                        <td key={colIndex} style={{ border: "1px solid gray" }}>
                          <input
                            type="text"
                            value={section[rowIndex]}
                            style={{
                              background: "rgba(173, 171, 171, 0.29)",
                              width: "120px",
                            }}
                            readOnly
                          />
                        </td>
                      );
                    } else if (colIndex === 3) {
                      return (
                        <td key={colIndex} style={{ border: "1px solid gray" }}>
                          <input
                            type="number"
                            value={cell}
                            style={{ width: "80px" }}
                            onChange={(e) =>
                              handleChange(rowIndex, colIndex, e.target.value)
                            }
                          />
                        </td>
                      );
                    } else if (colIndex === 4) {
                      return (
                        <td key={colIndex} style={{ border: "1px solid gray" }}>
                          <input
                            type="time"
                            value={cell}
                            style={{ width: "80px" }}
                            onChange={(e) =>
                              handleChange(rowIndex, colIndex, e.target.value)
                            }
                          />
                        </td>
                      );
                    } else if (colIndex === 5) {
                      return (
                        <td key={colIndex} style={{ border: "1px solid gray" }}>
                          <input
                            type="text"
                            value={minutesToHHMM(Number(cell) || 0)}
                            style={{
                              background: "rgba(173, 171, 171, 0.29)",
                              width: "80px",
                            }}
                            readOnly
                          />
                        </td>
                      );
                    } else {
                      return (
                        <td key={colIndex} style={{ border: "1px solid gray" }}>
                          <input
                            type="text"
                            value={cell}
                            style={{ width: "60px" }}
                            onChange={(e) =>
                              handleChange(rowIndex, colIndex, e.target.value)
                            }
                          />
                        </td>
                      );
                    }
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="p-3">
        {result?.status === "ok" ? (
          <SuccessMsg
            head="Success"
            message={result?.message}
            show={show}
            setShow={setShow}
          />
        ) : (
          <FailedMsg
            head="Failed"
            message={`${result?.message}, ${result?.error}`}
            show={show}
            setShow={setShow}
          />
        )}
      </div>

      <div className="text-center">
        <button
          className="m-1 btn btn-sm btn-outline-danger"
          onClick={clearAll}
        >
          <FaEraser /> Clear All
        </button>
        <button
          className={`m-1 btn btn-sm btn-outline-success ${
            loading ? "disabled" : ""
          }`}
          onClick={sendToBackend}
        >
          <FaUpload /> Upload
        </button>
        {loading && <Loading />}
      </div>
    </div>
  );
};

export default PDDInsertion;
